import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

const alternatives = [
  {
    name: 'TENSION',
    fit: 'Couples who want conflict prevention + communication coaching in one app.',
    path: '/features/relationship-intelligence',
  },
  {
    name: 'Conversation Micro-Coach',
    fit: 'Couples optimizing text and difficult conversations before conflict escalates.',
    path: '/features/conversation-micro-coach',
  },
  {
    name: 'Guides',
    fit: 'Users who prefer practical self-serve frameworks before committing to a plan.',
    path: '/guides',
  },
];

export default function PairedAlternatives() {
  useSEO({
    title: 'Best Paired Alternatives for Couples',
    description:
      'Looking for alternatives to Paired? Compare practical options for communication support, conflict de-escalation, and relationship growth.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.alternatives as unknown as string[],
      ['paired alternatives', 'apps like paired', 'best paired alternatives'],
    ),
    canonical: '/alternatives/paired',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="grid gap-6 overflow-hidden rounded-3xl border border-fuchsia-200/60 bg-white/90 shadow-xl md:grid-cols-[1fr_0.95fr] md:items-center">
          <div className="p-6 md:p-10">
            <p className="inline-flex rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-fuchsia-800 ring-1 ring-fuchsia-200/80">
              Alternatives
            </p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
              Paired alternatives for modern couples
            </h1>
            <p className="mt-3 max-w-2xl text-neutral-600 md:text-lg">
              If you are comparing apps like Paired, start with the option that matches your relationship goals and
              communication style.
            </p>
          </div>
          <StockImage
            stockKey="coupleHomeRelax"
            width={800}
            height={640}
            priority
            alt="Couple relaxing together at home"
            className="h-52 w-full object-cover md:h-full md:min-h-[260px] md:rounded-none md:rounded-r-3xl"
          />
        </header>

        <StockHeroStrip stockKey="handsWalking" alt="Two people walking together, connected" />

        <section className="space-y-4">
          {alternatives.map((option) => (
            <Card
              key={option.name}
              className="border-fuchsia-100/90 bg-white/95 p-6 shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg md:p-7"
            >
              <h2 className="text-xl font-semibold text-neutral-900">{option.name}</h2>
              <p className="mt-2 text-neutral-700">{option.fit}</p>
              <Link to={option.path} className="mt-5 inline-flex">
                <Button className="bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white shadow-md">Open</Button>
              </Link>
            </Card>
          ))}
        </section>

        <Card className="border-fuchsia-200/80 bg-gradient-to-r from-fuchsia-50/95 to-violet-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Need a direct side-by-side?</h2>
          <p className="mt-2 text-neutral-700">
            Use our detailed comparison page to evaluate fit before subscribing.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/compare/tension-vs-paired">
              <Button className="bg-fuchsia-700 hover:bg-fuchsia-800">View TENSION vs Paired</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-fuchsia-300 bg-white/90">
                Compare plans
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
